import { randomUUID } from 'node:crypto';
import { Router } from 'express';
import { z } from 'zod';
import { requireAdmin, requireAuth, type AuthenticatedRequest } from './auth';
import { autoAssignJourneyGuardians } from './autoGuardianSelection';
import { pool } from './db';
import { journeyNotificationRecipients, notifyUsers } from './notifications';
import { ensurePaymentObligations } from './paymentService';
import { recordJourneyEvent, recordPairEvent } from './relationshipEvents';

export const adminJourneyActionsRouter = Router();

const meetingFields = z.object({
  startsAt: z.iso.datetime({ offset: true }),
  timeZone: z.string().min(1).max(80),
});
const actionSchema = z.discriminatedUnion('action', [
  meetingFields.extend({ action: z.literal('meeting_propose') }),
  meetingFields.extend({ action: z.literal('meeting_replace'), meetingId: z.uuid() }),
  z.object({ action: z.literal('meeting_confirm'), meetingId: z.uuid() }),
  z.object({ action: z.literal('meeting_cancel'), meetingId: z.uuid(), reason: z.string().trim().max(500).default('') }),
  z.object({ action: z.literal('photo_request') }),
  z.object({ action: z.literal('photo_decision'), decision: z.enum(['approved', 'declined']) }),
  z.object({ action: z.literal('photo_reveal') }),
  z.object({ action: z.literal('photo_hide') }),
  z.object({ action: z.literal('journey_close') }),
]);

type JourneyContext = {
  journeyId: string;
  requestId: string;
  targetId: string;
  counterpartId: string;
  targetName: string;
  counterpartName: string;
};

const loadContext = async (targetId: string, journeyId: string): Promise<JourneyContext | null> => {
  const result = await pool.query<JourneyContext>(
    `SELECT journey.id AS "journeyId",journey.request_id AS "requestId",$1::uuid AS "targetId",
       CASE WHEN journey.first_user_id=$1 THEN journey.second_user_id ELSE journey.first_user_id END AS "counterpartId",
       target.display_name AS "targetName",counterpart.display_name AS "counterpartName"
     FROM journeys journey
     JOIN users target ON target.id=$1
     JOIN users counterpart ON counterpart.id=CASE WHEN journey.first_user_id=$1
       THEN journey.second_user_id ELSE journey.first_user_id END
     WHERE journey.id=$2 AND journey.status='active'
       AND $1 IN (journey.first_user_id,journey.second_user_id)`,
    [targetId, journeyId],
  );
  return result.rows[0] ?? null;
};

const auditAction = async (adminId: string, context: JourneyContext, action: string): Promise<void> => {
  await pool.query(
    `INSERT INTO audit_events (user_id,event_type,details)
     VALUES ($1,'admin_journey_action',$2::jsonb)`,
    [adminId, JSON.stringify({ targetUserId: context.targetId, journeyId: context.journeyId, action })],
  );
};

const validateMeetingTime = (startsAt: string, timeZone: string): boolean => {
  if (Date.parse(startsAt) <= Date.now()) return false;
  try { new Intl.DateTimeFormat('en', { timeZone }); return true; }
  catch { return false; }
};

const proposeMeeting = async (context: JourneyContext, startsAt: string, timeZone: string): Promise<void> => {
  const completed = await pool.query<{ count: number }>(
    `SELECT COUNT(*)::int AS count FROM meeting_proposals
     WHERE journey_id=$1 AND status='completed'`, [context.journeyId],
  );
  const sessionNumber = completed.rows[0].count + 1;
  if (sessionNumber > 3) throw new Error('SESSION_LIMIT');
  const id = randomUUID();
  await pool.query(
    `INSERT INTO meeting_proposals
       (id,journey_id,proposed_by,starts_at,time_zone,video_required,session_number)
     VALUES ($1,$2,$3,$4,$5,TRUE,$6)`,
    [id, context.journeyId, context.targetId, startsAt, timeZone, sessionNumber],
  );
  await recordJourneyEvent(context.journeyId, context.targetId, 'meeting_proposed', {
    meetingId: id, startsAt, timeZone,
  });
  await notifyUsers([context.counterpartId], {
    type: 'meeting_proposed', title: 'اقتراح موعد جديد',
    body: `${context.targetName} اقترح موعدًا لمكالمة الفيديو.`,
    journeyId: context.journeyId, requestId: context.requestId,
    dedupeKey: `meeting-proposed:${id}`,
  });
};

const confirmMeeting = async (context: JourneyContext, meetingId: string): Promise<void> => {
  const result = await pool.query(
    `UPDATE meeting_proposals SET status='confirmed'
     WHERE id=$1 AND journey_id=$2 AND proposed_by<>$3 AND status='proposed' RETURNING id`,
    [meetingId, context.journeyId, context.targetId],
  );
  if (!result.rowCount) throw new Error('MEETING_STATE');
  await recordJourneyEvent(context.journeyId, context.targetId, 'meeting_confirmed', { meetingId });
  await autoAssignJourneyGuardians(context.journeyId);
  await ensurePaymentObligations(meetingId);
  const recipients = await journeyNotificationRecipients(context.journeyId, context.targetId);
  await notifyUsers(recipients.userIds, {
    type: 'meeting_confirmed', title: 'تم الاتفاق على الموعد',
    body: 'وافق الطرفان على الموعد وأصبح جاهزًا لتأكيد وليّي الأمر.',
    journeyId: context.journeyId, requestId: context.requestId,
    dedupeKey: `meeting-confirmed:${meetingId}`,
  });
};

const replaceMeeting = async (
  context: JourneyContext, meetingId: string, startsAt: string, timeZone: string,
): Promise<void> => {
  const client = await pool.connect();
  let createdId = '';
  try {
    await client.query('BEGIN');
    const cancelled = await client.query<{ session_number: number }>(
      `UPDATE meeting_proposals SET status='cancelled'
       WHERE id=$1 AND journey_id=$2 AND status IN ('proposed','confirmed') RETURNING session_number`,
      [meetingId, context.journeyId],
    );
    if (!cancelled.rowCount) throw new Error('MEETING_STATE');
    await client.query('DELETE FROM meeting_schedule_confirmations WHERE meeting_id=$1', [meetingId]);
    await client.query(
      'UPDATE meeting_call_sessions SET left_at=NOW() WHERE meeting_id=$1 AND left_at IS NULL', [meetingId],
    );
    createdId = randomUUID();
    await client.query(
      `INSERT INTO meeting_proposals
        (id,journey_id,proposed_by,starts_at,time_zone,video_required,session_number)
       VALUES ($1,$2,$3,$4,$5,TRUE,$6)`,
      [createdId, context.journeyId, context.targetId, startsAt, timeZone, cancelled.rows[0].session_number],
    );
    await client.query('COMMIT');
  } catch (error) { await client.query('ROLLBACK'); throw error; }
  finally { client.release(); }
  await recordJourneyEvent(context.journeyId, context.targetId, 'meeting_cancelled', { meetingId, replaced: true });
  await recordJourneyEvent(context.journeyId, context.targetId, 'meeting_proposed', {
    meetingId: createdId, startsAt, timeZone,
  });
  await notifyUsers([context.counterpartId], {
    type: 'meeting_proposed', title: 'اقتراح موعد بديل',
    body: `${context.targetName} اقترح موعدًا جديدًا لمكالمة الفيديو.`,
    journeyId: context.journeyId, requestId: context.requestId,
    dedupeKey: `meeting-proposed:${createdId}`,
  });
};

const cancelMeeting = async (context: JourneyContext, meetingId: string, reason: string): Promise<void> => {
  const result = await pool.query(
    `UPDATE meeting_proposals SET status='cancelled'
     WHERE id=$1 AND journey_id=$2 AND status IN ('proposed','confirmed') RETURNING id`,
    [meetingId, context.journeyId],
  );
  if (!result.rowCount) throw new Error('MEETING_STATE');
  await pool.query('UPDATE meeting_call_sessions SET left_at=NOW() WHERE meeting_id=$1 AND left_at IS NULL', [meetingId]);
  await recordJourneyEvent(context.journeyId, context.targetId, 'meeting_cancelled', { meetingId, reason });
  const recipients = await journeyNotificationRecipients(context.journeyId, context.targetId);
  await notifyUsers(recipients.userIds, {
    type: 'meeting_cancelled', title: 'أُلغي موعد مكالمة الفيديو',
    body: reason ? `أُلغي الموعد. السبب: ${reason}` : 'أُلغي الموعد ويمكن اقتراح موعد آخر.',
    journeyId: context.journeyId, requestId: context.requestId,
    dedupeKey: `meeting-cancelled:${meetingId}`,
  });
};

const runPhotoAction = async (
  context: JourneyContext, action: 'photo_request' | 'photo_decision' | 'photo_reveal' | 'photo_hide',
  decision?: 'approved' | 'declined',
): Promise<void> => {
  if (action === 'photo_request') {
    const result = await pool.query(
      `INSERT INTO profile_photo_access (owner_user_id,viewer_user_id,status,requested_by)
       SELECT $1,$2,'requested',$2 WHERE EXISTS (SELECT 1 FROM profile_photos WHERE user_id=$1)
       ON CONFLICT (owner_user_id,viewer_user_id) DO UPDATE SET
       status=CASE WHEN profile_photo_access.status='revealed' THEN 'revealed' ELSE 'requested' END,
       requested_by=$2,updated_at=NOW() RETURNING status`,
      [context.counterpartId, context.targetId],
    );
    if (!result.rowCount) throw new Error('PHOTO_MISSING');
    if (result.rows[0].status === 'requested') {
      await recordPairEvent(context.counterpartId, context.targetId, context.targetId, 'photo_requested', {});
      await notifyUsers([context.counterpartId], {
        type: 'photo_request', title: 'طلب رؤية الصورة',
        body: `${context.targetName} يطلب رؤية صورتك الشخصية.`, requestId: context.requestId,
        dedupeKey: `photo-request:${context.counterpartId}:${context.targetId}:${Date.now()}`,
      });
    }
    return;
  }
  if (action === 'photo_decision') {
    const status = decision === 'approved' ? 'revealed' : 'declined';
    const result = await pool.query(
      `UPDATE profile_photo_access SET status=$1,updated_at=NOW()
       WHERE owner_user_id=$2 AND viewer_user_id=$3 AND status='requested' RETURNING status`,
      [status, context.targetId, context.counterpartId],
    );
    if (!result.rowCount) throw new Error('PHOTO_REQUEST_MISSING');
    await recordPairEvent(context.targetId, context.counterpartId, context.targetId,
      decision === 'approved' ? 'photo_approved' : 'photo_declined', {});
    await notifyUsers([context.counterpartId], {
      type: 'photo_decision', title: decision === 'approved'
        ? 'تمت الموافقة على عرض الصورة' : 'تم الاعتذار عن عرض الصورة',
      body: decision === 'approved' ? `${context.targetName} سمح لك برؤية الصورة الشخصية.`
        : `${context.targetName} اعتذر عن عرض الصورة الشخصية.`, requestId: context.requestId,
      dedupeKey: `photo-decision:${context.targetId}:${context.counterpartId}:${status}:${Date.now()}`,
    });
    return;
  }
  if (action === 'photo_reveal') {
    const result = await pool.query(
      `INSERT INTO profile_photo_access (owner_user_id,viewer_user_id,status,requested_by)
       SELECT $1,$2,'revealed',$1 WHERE EXISTS (SELECT 1 FROM profile_photos WHERE user_id=$1)
       ON CONFLICT (owner_user_id,viewer_user_id) DO UPDATE SET
       status='revealed',requested_by=$1,updated_at=NOW() RETURNING status`,
      [context.targetId, context.counterpartId],
    );
    if (!result.rowCount) throw new Error('PHOTO_MISSING');
    await recordPairEvent(context.targetId, context.counterpartId, context.targetId, 'photo_shared', {});
    await notifyUsers([context.counterpartId], {
      type: 'photo_decision', title: 'تمت مشاركة صورة معك',
      body: `${context.targetName} أتاح لك رؤية الصورة الشخصية.`, requestId: context.requestId,
      dedupeKey: `photo-shared:${context.targetId}:${context.counterpartId}:${Date.now()}`,
    });
    return;
  }
  const result = await pool.query(
    `DELETE FROM profile_photo_access
     WHERE owner_user_id=$1 AND viewer_user_id=$2 AND status='revealed' RETURNING owner_user_id`,
    [context.targetId, context.counterpartId],
  );
  if (result.rowCount) await recordPairEvent(
    context.targetId, context.counterpartId, context.targetId, 'photo_hidden', {},
  );
};

adminJourneyActionsRouter.post(
  '/users/:userId/journeys/:journeyId/action', requireAuth, requireAdmin,
  async (request: AuthenticatedRequest, response) => {
    const userId = z.uuid().safeParse(request.params.userId);
    const journeyId = z.uuid().safeParse(request.params.journeyId);
    const parsed = actionSchema.safeParse(request.body);
    if (!userId.success || !journeyId.success || !parsed.success) {
      response.status(400).json({ error: 'Invalid journey action' }); return;
    }
    const context = await loadContext(userId.data, journeyId.data);
    if (!context) { response.status(404).json({ error: 'Active participant journey not found' }); return; }
    try {
      const action = parsed.data;
      if ('startsAt' in action && !validateMeetingTime(action.startsAt, action.timeZone)) {
        response.status(400).json({ error: 'Invalid meeting time' }); return;
      }
      if (action.action === 'meeting_propose') await proposeMeeting(context, action.startsAt, action.timeZone);
      else if (action.action === 'meeting_confirm') await confirmMeeting(context, action.meetingId);
      else if (action.action === 'meeting_replace') {
        await replaceMeeting(context, action.meetingId, action.startsAt, action.timeZone);
      } else if (action.action === 'meeting_cancel') await cancelMeeting(context, action.meetingId, action.reason);
      else if (action.action === 'journey_close') {
        const client = await pool.connect();
        try {
          await client.query('BEGIN');
          await client.query("UPDATE journeys SET status='closed',updated_at=NOW() WHERE id=$1", [context.journeyId]);
          await client.query('DELETE FROM journey_contact_grants WHERE journey_id=$1', [context.journeyId]);
          await client.query("UPDATE family_invitations SET status='revoked' WHERE journey_id=$1 AND status<>'revoked'", [context.journeyId]);
          await client.query('UPDATE journey_memberships SET is_active=FALSE WHERE journey_id=$1', [context.journeyId]);
          await client.query(
            "UPDATE decision_rounds SET status='resolved',outcome='closed',resolved_at=NOW() WHERE journey_id=$1 AND status='open'",
            [context.journeyId],
          );
          await client.query('COMMIT');
        } catch (error) { await client.query('ROLLBACK'); throw error; }
        finally { client.release(); }
      } else await runPhotoAction(context, action.action, 'decision' in action ? action.decision : undefined);
      await auditAction(request.authUser!.id, context, action.action);
      response.json({ saved: true });
    } catch (error) {
      const code = error instanceof Error ? error.message : '';
      if (['SESSION_LIMIT', 'MEETING_STATE', 'PHOTO_MISSING', 'PHOTO_REQUEST_MISSING'].includes(code)) {
        response.status(409).json({ error: code }); return;
      }
      if (typeof error === 'object' && error && 'code' in error && error.code === '23505') {
        response.status(409).json({ error: 'ACTIVE_MEETING_EXISTS' }); return;
      }
      throw error;
    }
  },
);
